import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  TextField,
  Button,
  Divider,
  Alert,
  Grid,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Add as AddIcon,
} from '@mui/icons-material';

const PLACEHOLDERS = [
  '{{ recipient_name }}',
  '{{ service_name }}',
  '{{ service_names }}',
  '{{ services_list }}',
  '{{ client_name }}',
  '{{ expiration_date }}',
  '{{ days_until_expiration }}',
  '{{ renewal_cost }}',
];

const PREVIEW_SAMPLE = {
  recipient_name: 'BillMaster Admin',
  service_name: 'Maintenance - Deluxe',
  client_name: 'Ευθυμία Αντωνίου',
  expiration_date: '05/08/2026',
  days_until_expiration: '7',
  renewal_cost: '267,84 €',
  logo_url: 'https://billmaster.netgen.gr/logo.png',
};

const renderTemplatePreview = (template, sampleData) => {
  if (!template) return '';
  let rendered = template;
  Object.entries(sampleData).forEach(([key, value]) => {
    rendered = rendered.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), value);
    rendered = rendered.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
  });
  return rendered;
};

const EmailBodyEditor = ({ subject, bodyHtml, onSubjectChange, onBodyChange, disabled }) => {
  const previewSubject = useMemo(
    () => renderTemplatePreview(subject, PREVIEW_SAMPLE),
    [subject]
  );

  const previewHtml = useMemo(
    () => renderTemplatePreview(bodyHtml, PREVIEW_SAMPLE),
    [bodyHtml]
  );

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        label="Θέμα email"
        value={subject || ''}
        onChange={(e) => onSubjectChange(e.target.value)}
        disabled={disabled}
        helperText="Χρησιμοποιήστε {client_name}, {service_name}, {expiration_date}, {days_until_expiration}"
      />

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <TextField
            fullWidth
            multiline
            minRows={16}
            maxRows={28}
            label="HTML σώμα email"
            value={bodyHtml || ''}
            onChange={(e) => onBodyChange(e.target.value)}
            disabled={disabled}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                lineHeight: 1.5,
              },
            }}
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper
            variant="outlined"
            sx={{
              height: '100%',
              minHeight: { xs: 320, lg: 420 },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: '#f5f5f5',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Προεπισκόπηση (με δείγμα δεδομένων)
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {previewSubject || '—'}
              </Typography>
            </Box>
            <Box
              component="iframe"
              title="email-preview"
              srcDoc={previewHtml || '<p style="font-family:sans-serif;color:#666;padding:16px;">Δεν υπάρχει HTML προς εμφάνιση.</p>'}
              sx={{
                flex: 1,
                width: '100%',
                border: 0,
                bgcolor: '#fff',
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Διαθέσιμα placeholders (Jinja2):
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {PLACEHOLDERS.map((ph) => (
            <Chip key={ph} label={ph} size="small" variant="outlined" />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

const WarningDaysEditor = ({ label, days, onChange, disabled }) => {
  const [input, setInput] = React.useState('');

  const addDay = () => {
    const value = parseInt(input, 10);
    if (!value || value <= 0) return;
    if (days.includes(value)) {
      setInput('');
      return;
    }
    onChange([...days, value].sort((a, b) => b - a));
    setInput('');
  };

  const removeDay = (day) => {
    onChange(days.filter((d) => d !== day));
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {days.map((day) => (
          <Chip
            key={day}
            label={`${day} ημέρες πριν`}
            onDelete={disabled ? undefined : () => removeDay(day)}
            color="primary"
            variant="outlined"
          />
        ))}
        {days.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Δεν έχουν οριστεί ημέρες ειδοποίησης.
          </Typography>
        )}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small"
          type="number"
          label="Προσθήκη ημερών"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={disabled}
          inputProps={{ min: 1 }}
          sx={{ maxWidth: { sm: 160 } }}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addDay}
          disabled={disabled || !input}
        >
          Προσθήκη
        </Button>
      </Stack>
    </Box>
  );
};

const SettingsNotificationsTab = ({ settings, onChange, disabled }) => {
  const update = (field, value) => {
    onChange({ ...settings, [field]: value });
  };

  const handleRecipientChange = (field) => (event) => {
    const next = { ...settings, [field]: event.target.checked };
    if (!next.send_to_admin && !next.send_to_client) {
      next.send_to_admin = true;
    }
    onChange(next);
  };

  return (
    <Stack spacing={2}>
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Ρυθμίσεις ειδοποιήσεων
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <EmailIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Email ειδοποιήσεις
                </Typography>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.email_enabled}
                    onChange={(e) => update('email_enabled', e.target.checked)}
                    disabled={disabled}
                  />
                }
                label="Ενεργές email ειδοποιήσεις λήξης υπηρεσιών"
              />
              <Box sx={{ mt: 2, opacity: settings.email_enabled ? 1 : 0.5 }}>
                <WarningDaysEditor
                  label="Αποστολή email πριν τη λήξη"
                  days={settings.email_warning_days || []}
                  onChange={(days) => update('email_warning_days', days)}
                  disabled={disabled || !settings.email_enabled}
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Παραλήπτες email
                </Typography>
                <Stack spacing={0.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.send_to_admin}
                        onChange={handleRecipientChange('send_to_admin')}
                        disabled={disabled || !settings.email_enabled}
                      />
                    }
                    label="Αποστολή στον διαχειριστή (admin)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.send_to_client}
                        onChange={handleRecipientChange('send_to_client')}
                        disabled={disabled || !settings.email_enabled}
                      />
                    }
                    label="Αποστολή και στον πελάτη"
                  />
                </Stack>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <NotificationsIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  In-app ειδοποιήσεις
                </Typography>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.in_app_enabled}
                    onChange={(e) => update('in_app_enabled', e.target.checked)}
                    disabled={disabled}
                  />
                }
                label="Ενεργές ειδοποιήσεις στην εφαρμογή"
              />
              <Box sx={{ mt: 2, opacity: settings.in_app_enabled ? 1 : 0.5 }}>
                <WarningDaysEditor
                  label="Δημιουργία in-app ειδοποίησης πριν τη λήξη"
                  days={settings.in_app_warning_days || []}
                  onChange={(days) => update('in_app_warning_days', days)}
                  disabled={disabled || !settings.in_app_enabled}
                />
              </Box>
            </Box>

            <Alert severity="info">
              Το cron εκτελείται καθημερινά στις 02:00. Αν χαθεί μια ημέρα, γίνεται αυτόματο catch-up στην επόμενη εκτέλεση.
            </Alert>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Σώμα Email
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <EmailBodyEditor
            subject={settings.email_subject}
            bodyHtml={settings.email_body_html}
            onSubjectChange={(value) => update('email_subject', value)}
            onBodyChange={(value) => update('email_body_html', value)}
            disabled={disabled || !settings.email_enabled}
          />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default SettingsNotificationsTab;
